import { useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Heading from "@atlaskit/heading";
import { Text } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import { ServiceCollectionHeader } from "@atlassian/growth-shared-ui";
import { JiraBackground } from "@atlassian/product-wireframes";

import { useAnalyticsClient } from "@/analytics/wac-analytics/analytics-client";
import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper
} from "@/components/signup/utils";
import { ProvisioningStatus } from "@/libraries/bxp-web-stargate-client/src";
import { wacToken } from "@/libraries/design/foundations";

import { DwellTimeWrapper } from "../../components/dwell-time-wrapper";
import { ProvisioningProgressBar } from "../../sections/provisioning-progress-bar";

import { ServiceCollectionBackgroundSvg } from "./components/service-collection-background-svg";
import { ServiceCollectionSvg } from "./components/service-collection-svg";

const modalWrapperStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%"
});

const modalStyles = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: 972,
    height: 568,
    backgroundColor: wacToken("color.neutral.light0"),
    borderRadius: token("border.radius"),
    boxShadow: token("elevation.shadow.overlay")
});

const headerContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    paddingBlockStart: token("space.600"),
    paddingInlineStart: token("space.600"),
    maxWidth: "50%"
});

const headingStyles = css({
    marginBlockStart: token("space.300"),
    marginBlockEnd: token("space.250")
});

const progressTextStyles = css({
    color: token("color.text.subtle"),
    marginBlockEnd: token("space.150")
});

const animationContainer = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 484,
    height: "100%"
});

const backgroundImageStyles = css({
    position: "absolute",
    height: "100%",
    width: "auto",
    insetInlineEnd: 0,
    insetBlockStart: 0,
    zIndex: 0,
    overflow: "hidden",
    borderStartEndRadius: token("border.radius"),
    borderEndEndRadius: token("border.radius")
});

const serviceCollectionStyles = css({
    zIndex: 1
});

export interface ServiceCollectionProvisioningScreenProps {
    provisioningStep?: string;
    provisioningStatus?: ProvisioningStatus;
}

const ServiceCollectionProvisioningScreen = ({
    provisioningStep,
    provisioningStatus
}: ServiceCollectionProvisioningScreenProps = {}) => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        if (analyticsClient) {
            createAnalyticsEvent({
                eventName: "serviceCollectionProvisioningPageViewed",
                eventComponent: "serviceCollectionProvisioningPage",
                event: "viewed",
                attributes: {
                    isJsmGrowth220Enabled: true
                }
            }).fire("signup");
        }
    }, [analyticsClient, createAnalyticsEvent]);

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={
                UfoCustomTimingKey.SERVICE_COLLECTION_PROVISIONING_VIEW_DWELL_TIME
            }
        >
            <DwellTimeWrapper
                eventData={{
                    eventName: "serviceCollectionProvisioningPageDwelled",
                    eventComponent: "serviceCollectionProvisioningPage",
                    event: "dwelled",
                    actionSubject: "serviceCollectionProvisioningPage",
                    source: "bxp-web-platform-wac-confirmation-page",
                    attributes: {
                        isJsmGrowth220Enabled: true
                    }
                }}
                initialPollInterval={2000}
                idleTimeout={120000}
                nextPollIntervalCallback={(x: number) => x + 2000}
            >
                <JiraBackground>
                    <div css={modalWrapperStyles}>
                        <div css={modalStyles}>
                            <div css={headerContainerStyles}>
                                <ServiceCollectionHeader />
                                <div css={headingStyles}>
                                    <Heading size="large">
                                        <FormattedMessage
                                            id="SignupProvisioning.title.serviceCollection"
                                            defaultMessage="Adding Service Collection"
                                        />
                                    </Heading>
                                </div>
                                <div css={progressTextStyles}>
                                    <Text variant="body">
                                        <FormattedMessage
                                            id="SignupProvisioning.inProgressText.serviceCollection.waitingForProvisioning"
                                            defaultMessage="Adding Service Collection. Less than two minutes remaining..."
                                        />
                                    </Text>
                                </div>
                                <ProvisioningProgressBar
                                    provisioningStep={provisioningStep}
                                    provisioningStatus={provisioningStatus}
                                />
                            </div>
                            <div css={animationContainer}>
                                <div css={backgroundImageStyles}>
                                    <ServiceCollectionBackgroundSvg />
                                </div>
                                <div css={serviceCollectionStyles}>
                                    <ServiceCollectionSvg />
                                </div>
                            </div>
                        </div>
                    </div>
                </JiraBackground>
            </DwellTimeWrapper>
        </UfoCustomTimingWrapper>
    );
};

export default ServiceCollectionProvisioningScreen;
